import { formatPhoneNumber } from "@/helpers/phone-helpers";
import IContact from "@/interfaces/contact.interface";
import { ConsentStatusMessages } from "@/messages/consent-status.messages";

type UseContactDataReturn = Partial<IContact> & {
  fullName: string;
  phoneNumber: string;
  concatPhoneNumber: string;
  consentStatusTranslated?: string;
  consentBgColor?: "success" | "error" | "warning" | "info" | undefined;
  activeLabel?: string;
  cityDisplay: string;
  stateDisplay: string;
};

type UseContactDataProps = {
  contact: IContact | null;
  messages: ConsentStatusMessages & { activeYes?: string; activeNo?: string };
};

function normalizeConsentStatusCode(value: string | null | undefined): string {
  return (value ?? "")
    .toString()
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "_");
}

function isConsentPending(contact: IContact | null): boolean {
  if (!contact) return false;
  const hasConsentStatus = Boolean(contact.consentStatus?.toString().trim());
  const consentDate = contact.consentDate;
  const hasConsentDate =
    consentDate !== null && consentDate !== undefined && String(consentDate).trim() !== "";
  return !hasConsentDate && !hasConsentStatus;
}

function mapConsentStatusToDisplay(
  status: string,
  messages: ConsentStatusMessages
): { label: string; color: "success" | "error" | "warning" | "info" | undefined } {
  const normalized = normalizeConsentStatusCode(status);

  switch (normalized) {
    case "completed":
    case "concedido":
      return { label: messages.completed, color: "success" };
    case "canceled":
    case "cancelado":
      return { label: messages.canceled, color: "error" };
    case "not_completed":
    case "notcompleted":
    case "nao_concedido":
      return { label: messages.notCompleted, color: "warning" };
    default:
      return { label: "-", color: undefined };
  }
}

function useContactData({ contact, messages }: UseContactDataProps): UseContactDataReturn {
  const fullName = contact
    ? `${contact.firstName ?? ""}${contact.lastName ? ` ${contact.lastName}` : ""}`.trim()
    : "";

  const concatPhoneNumber = contact
    ? `${contact.ddiMobile}${contact.dddMobile}${contact.mobilePhone}`
    : "";

  const phoneNumber = formatPhoneNumber(concatPhoneNumber);

  let consentStatusTranslated = "";
  let consentBgColor: "success" | "error" | "warning" | "info" | undefined = undefined;

  if (isConsentPending(contact)) {
    consentStatusTranslated = messages.pending;
    consentBgColor = "warning";
  } else if (contact?.consentStatus) {
    const mapped = mapConsentStatusToDisplay(contact.consentStatus, messages);
    consentStatusTranslated = mapped.label;
    consentBgColor = mapped.color;
  } else {
    consentStatusTranslated = messages.pending;
    consentBgColor = "warning";
  }

  const activeLabel =
    contact?.active === false
      ? (messages.activeNo ?? "Não")
      : (messages.activeYes ?? "Sim");

  const cityDisplay = contact?.addressCity?.trim() || "-";
  const stateDisplay =
    contact?.addressStateUf?.trim() || contact?.addressState?.trim() || "-";

  return {
    ...contact,
    fullName,
    phoneNumber,
    concatPhoneNumber,
    consentStatusTranslated,
    consentBgColor,
    activeLabel,
    cityDisplay,
    stateDisplay,
  };
}

export default useContactData;
